import React, { useState, useRef } from 'react';
import { importApi, ImportValidationResult } from '../services/importApi';
import { toast } from 'react-hot-toast';

interface ImportDlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

const ImportDlowModal: React.FC<ImportDlowModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jsonData, setJsonData] = useState<string>('');
  const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      toast.error('Vui lòng chọn file JSON hợp lệ');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Kích thước file phải nhỏ hơn 10MB');
      return;
    }

    // Read file and populate jsonData
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        setJsonData(JSON.stringify(parsed, null, 2));
        setSelectedFile(file);
        toast.success('File đã được tải lên. Bạn có thể import ngay hoặc kiểm tra trước.');
      } catch (err) {
        toast.error('File JSON không hợp lệ');
      }
    };
    reader.readAsText(file);
  };

  const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonData(event.target.value);
    setValidationResult(null);
    setSelectedFile(null);
  };

  const validateJson = async (file?: File) => {
    setValidationResult(null);
    setIsValidating(true);

    try {
      let result: ImportValidationResult;
      
      if (file) {
        // Validate using file
        result = await importApi.validateDlowJson(file);
      } else {
        // Validate using JSON text
        if (!jsonData.trim()) {
          toast.error('Vui lòng nhập dữ liệu JSON hoặc chọn file');
          return;
        }

        // Parse to check validity
        try {
          JSON.parse(jsonData);
        } catch {
          toast.error('Dữ liệu JSON không hợp lệ');
          return;
        }

        // Create a blob and validate
        const blob = new Blob([jsonData], { type: 'application/json' });
        const tempFile = new File([blob], 'data.json', { type: 'application/json' });
        result = await importApi.validateDlowJson(tempFile);
      }

      setValidationResult(result);
      
      if (result.success) {
        toast.success('Dữ liệu hợp lệ! Sẵn sàng để import');
      } else {
        toast.error(result.message || 'Validation thất bại');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Validation thất bại');
      setValidationResult({
        success: false,
        message: error.response?.data?.message || 'Validation thất bại'
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!jsonData.trim()) {
      toast.error('Vui lòng nhập dữ liệu JSON hoặc chọn file');
      return;
    }

    // Basic JSON validation
    try {
      JSON.parse(jsonData);
    } catch {
      toast.error('Dữ liệu JSON không hợp lệ. Vui lòng kiểm tra cú pháp.');
      return;
    }

    setIsImporting(true);
    try {
      // Create file from jsonData
      const blob = new Blob([jsonData], { type: 'application/json' });
      const file = selectedFile || new File([blob], 'import.json', { type: 'application/json' });
      
      const result = await importApi.importFromDlowJson(file);
      
      if (result.success) {
        toast.success('Import sách thành công!');
        
        // Reset and close modal
        setTimeout(() => {
          handleClose();
          onImportSuccess();
        }, 1000);
      } else {
        toast.error(result.message || 'Import thất bại');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Import thất bại');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setJsonData('');
    setValidationResult(null);
    setIsValidating(false);
    setIsImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setJsonData('');
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Import Sách từ External JSON
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isImporting}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* File Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload File JSON
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              disabled={isValidating || isImporting}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
            />
            <p className="mt-2 text-xs text-gray-500">
              Kích thước tối đa: 10MB • Định dạng: JSON
            </p>
          </div>

          {/* Divider */}
          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">hoặc</span>
            </div>
          </div>

          {/* JSON Data Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dữ liệu JSON
            </label>
            <textarea
              value={jsonData}
              onChange={handleJsonChange}
              placeholder="Paste dữ liệu JSON vào đây..."
              disabled={isValidating || isImporting}
              className="w-full h-48 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            />
            {jsonData && !validationResult && !isValidating && (
              <button
                onClick={() => validateJson()}
                className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
              >
                Kiểm tra JSON (Xem Preview)
              </button>
            )}
          </div>

          {/* Validating State */}
          {isValidating && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center text-blue-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm">Đang kiểm tra dữ liệu JSON...</span>
              </div>
            </div>
          )}

          {/* Validation Result */}
          {validationResult && (
            <div className={`mb-6 p-4 rounded-lg border-l-4 ${
              validationResult.success 
                ? 'bg-green-50 border-green-500' 
                : 'bg-red-50 border-red-500'
            }`}>
              <div className="flex items-start">
                {validationResult.success ? (
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <div className="flex-1">
                  <h4 className={`font-semibold mb-1 ${
                    validationResult.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {validationResult.success ? 'Dữ liệu hợp lệ' : 'Dữ liệu không hợp lệ'}
                  </h4>
                  <p className={validationResult.success ? 'text-green-700 text-sm' : 'text-red-700 text-sm'}>
                    {validationResult.message}
                  </p>

                  {/* Preview */}
                  {validationResult.success && validationResult.data?.preview && (
                    <div className="mt-3 p-3 bg-white rounded-md">
                      <h5 className="font-medium text-gray-900 mb-2 text-sm">Thông tin sách</h5>
                      
                      {/* Cover Image */}
                      {validationResult.data.preview.cover_image && (
                        <div className="mb-3">
                          <img 
                            src={validationResult.data.preview.cover_image} 
                            alt="Book Cover"
                            className="w-32 h-48 object-cover rounded-md border border-gray-200"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Tiêu đề:</span>
                          <p className="font-medium text-gray-900">{validationResult.data.preview.title}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Số chương:</span>
                          <p className="font-medium text-gray-900">{validationResult.data.preview.sections_count}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Statistics */}
                  {validationResult.success && validationResult.data?.stats && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-md">
                      <h5 className="font-medium text-gray-900 mb-2 text-sm">Thống kê nội dung</h5>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-xl font-bold text-blue-600">
                            {validationResult.data.stats.sections}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">Chương</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-blue-600">
                            {validationResult.data.stats.lessons}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">Bài học</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-blue-600">
                            {validationResult.data.stats.items}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">Mục</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-600">
                            {validationResult.data.stats.images}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">Hình ảnh</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-purple-600">
                            {validationResult.data.stats.videos}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">Video</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-orange-600">
                            {validationResult.data.stats.scorm}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">SCORM</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Hướng dẫn</h3>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• Upload file JSON hoặc paste dữ liệu JSON vào textarea</li>
                  <li>• Có thể click "Kiểm tra JSON" để xem preview (tùy chọn)</li>
                  <li>• Hoặc click "Import Sách" trực tiếp để import ngay</li>
                  <li>• Hỗ trợ định dạng JSON bên ngoài với nested sections, lessons và nhiều loại nội dung</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={isImporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              onClick={handleImport}
              disabled={!jsonData.trim() || isImporting}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                jsonData.trim() && !isImporting
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isImporting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Đang import...
                </div>
              ) : (
                'Import Sách'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportDlowModal;

